import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OrganizationIcon } from '~/assets';
import AudiencesIcon from '~/assets/AudiencesIcon';
import ChevronIcon from '~/assets/ChevronIcon';
import { ShimmerText } from '~/components/Shimmer';
import { AudienceCondition, ConversationReference, ConversationReferenceType } from '~/types/api.graphql';
import { conditionIcon, conditionTitle } from '~/utils/audience';

import {
  AudienceMobileBody,
  AudiencePopoverBackButton,
  AudiencePopoverBackLabel,
  AudiencePopoverBackRow,
  AudiencePopoverInput,
  AudiencePopoverListSection,
  AudiencePopoverScrollList,
  AudienceTypeListItem,
  CloseButton,
  ConversationsPopoverGradient,
  MentionDropdownEmpty,
  MentionDropdownItem,
  MentionDropdownItemIcon,
  MentionDropdownItemName,
  SidebarHeader,
  SidebarTitleContainer,
  SidebarTitleText,
  StyledSearchIcon,
} from './AIInsightsSidebar.styles';
import { truncateName } from './mentionChipUtils';
import { useConversationReferenceMentions } from './useConversationReferenceMentions';

type PickerView = 'types' | 'search';

const AUDIENCE_TYPE_OPTIONS: Array<{ condition: AudienceCondition | null }> = [
  { condition: null },
  { condition: AudienceCondition.Pixel },
  { condition: AudienceCondition.CustomerList },
  { condition: AudienceCondition.Global },
  { condition: AudienceCondition.Segment },
  { condition: AudienceCondition.Lookalike },
  { condition: AudienceCondition.Pulse },
];

interface AudiencePickerContentProps {
  onSelectMention: (ref: ConversationReference) => void;
  onClose: () => void;
  isMobile?: boolean;
}

const AudiencePickerContent = ({ onSelectMention, onClose, isMobile = false }: AudiencePickerContentProps) => {
  const { t } = useTranslation();
  const [view, setView] = useState<PickerView>('types');
  const [selectedCondition, setSelectedCondition] = useState<AudienceCondition | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { results, loading } = useConversationReferenceMentions({
    query: view === 'search' ? searchQuery : null,
    filter: {
      entityType: ConversationReferenceType.Audience,
      ...(selectedCondition ? { audienceCondition: selectedCondition } : {}),
    },
  });

  const getTypeLabel = (condition: AudienceCondition | null): string =>
    condition ? conditionTitle(condition) : String(t('aiInsights.allAudiences'));

  const handleTypeClick = useCallback((condition: AudienceCondition | null) => {
    setSelectedCondition(condition);
    setSearchQuery('');
    setView('search');
  }, []);

  const handleSelectMention = useCallback(
    (ref: ConversationReference) => {
      onSelectMention(ref);
      onClose();
    },
    [onSelectMention, onClose],
  );

  const listRef = useRef<HTMLDivElement>(null);
  const [showTopGradient, setShowTopGradient] = useState(false);
  const [showBottomGradient, setShowBottomGradient] = useState(false);

  const updateGradients = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    setShowTopGradient(el.scrollTop > 0);
    setShowBottomGradient(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  useEffect(() => {
    requestAnimationFrame(updateGradients);
  }, [view, results, loading, updateGradients]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
    setShowTopGradient(false);
    setShowBottomGradient(false);
  }, [view]);

  const handleBack = useCallback(() => {
    setView('types');
    setSearchQuery('');
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (view === 'search') {
        handleBack();
      } else {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [view, handleBack, onClose]);

  const renderMobileHeader = () => {
    if (view === 'types') {
      return (
        <SidebarHeader>
          <SidebarTitleContainer>
            <SidebarTitleText>{t('aiInsights.addAudience')}</SidebarTitleText>
          </SidebarTitleContainer>
          <CloseButton type="button" onClick={onClose} aria-label={String(t('common.close'))}>
            ✕
          </CloseButton>
        </SidebarHeader>
      );
    }
    return (
      <SidebarHeader>
        <AudiencePopoverBackButton type="button" onClick={handleBack} aria-label={String(t('common.back'))}>
          <ChevronIcon />
        </AudiencePopoverBackButton>
        <SidebarTitleContainer>
          <SidebarTitleText>{t('aiInsights.selectAudience')}</SidebarTitleText>
        </SidebarTitleContainer>
        <CloseButton type="button" onClick={onClose} aria-label={String(t('common.close'))}>
          ✕
        </CloseButton>
      </SidebarHeader>
    );
  };

  const renderTypesView = () => (
    <AudiencePopoverListSection $fixed={false}>
      <ConversationsPopoverGradient $visible={showTopGradient} $position="top" />
      <AudiencePopoverScrollList ref={listRef} onScroll={updateGradients} $fixed={false}>
        {AUDIENCE_TYPE_OPTIONS.map(({ condition }) => (
          <AudienceTypeListItem key={condition ?? 'all'} type="button" onClick={() => handleTypeClick(condition)}>
            <MentionDropdownItemIcon>
              {condition ? conditionIcon(condition) : <AudiencesIcon />}
            </MentionDropdownItemIcon>
            <MentionDropdownItemName>{getTypeLabel(condition)}</MentionDropdownItemName>
          </AudienceTypeListItem>
        ))}
      </AudiencePopoverScrollList>
      <ConversationsPopoverGradient $visible={showBottomGradient} $position="bottom" />
    </AudiencePopoverListSection>
  );

  const renderSearchView = () => {
    const typeLabel = getTypeLabel(selectedCondition);
    return (
      <>
        {!isMobile && (
          <AudiencePopoverBackRow>
            <AudiencePopoverBackButton type="button" onClick={handleBack} aria-label={String(t('common.back'))}>
              <ChevronIcon />
            </AudiencePopoverBackButton>
            <AudiencePopoverBackLabel>{t('aiInsights.selectAudience')}</AudiencePopoverBackLabel>
          </AudiencePopoverBackRow>
        )}
        <AudiencePopoverInput
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
          onClearClick={() => setSearchQuery('')}
          clearable
          iconLeft={<StyledSearchIcon />}
          placeholder={`${String(t('common.search'))} ${typeLabel}...`}
          aria-label={String(t('common.search'))}
          label=""
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
        <AudiencePopoverListSection $fixed>
          <ConversationsPopoverGradient $visible={showTopGradient} $position="top" />
          <AudiencePopoverScrollList ref={listRef} onScroll={updateGradients} $fixed>
            {searchQuery.trim().length < 2 ? (
              <MentionDropdownEmpty>{t('aiInsights.typeToSearch')}</MentionDropdownEmpty>
            ) : loading ? (
              <>
                {[...Array(6)].map((_, i) => (
                  <ShimmerText key={i} width="100%" height="2rem" marginBottom="0.25rem" />
                ))}
              </>
            ) : results.length === 0 ? (
              <MentionDropdownEmpty>{t('aiInsights.noResults')}</MentionDropdownEmpty>
            ) : (
              results.map((item) => (
                <MentionDropdownItem
                  key={item.deepLink}
                  type="button"
                  $highlighted={false}
                  onClick={() => handleSelectMention(item)}
                >
                  <MentionDropdownItemIcon>
                    {item.type === ConversationReferenceType.Organization ? (
                      <OrganizationIcon />
                    ) : item.audienceCondition ? (
                      conditionIcon(item.audienceCondition)
                    ) : null}
                  </MentionDropdownItemIcon>
                  <MentionDropdownItemName title={item.name}>{truncateName(item.name)}</MentionDropdownItemName>
                </MentionDropdownItem>
              ))
            )}
          </AudiencePopoverScrollList>
          <ConversationsPopoverGradient $visible={showBottomGradient} $position="bottom" />
        </AudiencePopoverListSection>
      </>
    );
  };

  const content = view === 'types' ? renderTypesView() : renderSearchView();

  return (
    <>
      {isMobile && renderMobileHeader()}
      {isMobile ? <AudienceMobileBody>{content}</AudienceMobileBody> : content}
    </>
  );
};

export default AudiencePickerContent;
